// packages/chat-domain/src/state.ts
//
// The thread state machine. `status` is the single authority for who replies
// next (docs/chat-architecture.md §7). Encoding the allowed transitions here —
// rather than as scattered `if (status === ...)` checks at each call site — is
// the SSOT that keeps the v1 human flow and the Phase-B AI handoff coherent.
//
// Pure; no deps. The repo (chat-data) calls `assertThreadStatusTransition`
// inside its write transaction before flipping `chat_threads.status`.

import type { ChatThreadStatus } from './enums';

/**
 * Allowed `status` transitions. Each edge is annotated with the flow + phase
 * that drives it, so the machine doubles as documentation of the lifecycle.
 *
 *   open ─────────┬──► assigned     (v1: an agent picks up an unowned thread)
 *                 ├──► ai_handling  (Phase B: the bot answers first)
 *                 └──► closed       (v1: close without reply — spam / no-op)
 *   ai_handling ──┬──► assigned     (Phase B: a human replies and takes over directly)
 *                 ├──► open         (Phase B: the bot escalates — releases the
 *                 │                   thread to the UNASSIGNED queue so any human
 *                 │                   can take it over. The bot KEEPS replying
 *                 │                   while it waits there (see isAiAnswerable);
 *                 │                   it only goes silent once a human is assigned)
 *                 └──► closed       (Phase B: the bot resolved it)
 *   assigned ─────┬──► closed       (v1: the agent finalizes the thread)
 *                 └──► open         (v1: the agent releases it back to the queue)
 *   closed ───────────► open        (reopen — a participant messages a closed
 *                                     thread; v1.1, wired when reopen ships)
 *
 * Note: **takeover is not a status transition** — it stays `assigned` and only
 * re-points `assignedAgentUserId` (see `ChatRepo.assignThread`). Self-edges are
 * therefore intentionally absent. `assigned → ai_handling` stays ILLEGAL by
 * design: once a human owns a thread the bot must not silently grab it back.
 */
const ALLOWED_THREAD_TRANSITIONS: Record<ChatThreadStatus, readonly ChatThreadStatus[]> = {
  open: ['assigned', 'ai_handling', 'closed'],
  ai_handling: ['assigned', 'open', 'closed'],
  assigned: ['closed', 'open'],
  closed: ['open'],
};

/** True if `from → to` is a legal thread-status transition. */
export function canTransitionThreadStatus(from: ChatThreadStatus, to: ChatThreadStatus): boolean {
  return ALLOWED_THREAD_TRANSITIONS[from].includes(to);
}

/** A rejected thread-status transition. Caught at the write boundary and
 *  mapped to the app's error envelope (e.g. `ActionError.conflict`). */
export class ChatThreadStatusTransitionError extends Error {
  readonly from: ChatThreadStatus;
  readonly to: ChatThreadStatus;
  constructor(from: ChatThreadStatus, to: ChatThreadStatus) {
    super(`Illegal chat thread status transition: ${from} → ${to}`);
    this.name = 'ChatThreadStatusTransitionError';
    this.from = from;
    this.to = to;
  }
}

/** Throws {@link ChatThreadStatusTransitionError} unless `from → to` is legal.
 *  Returns `to` so it composes inline. */
export function assertThreadStatusTransition(
  from: ChatThreadStatus,
  to: ChatThreadStatus,
): ChatThreadStatus {
  if (!canTransitionThreadStatus(from, to)) {
    throw new ChatThreadStatusTransitionError(from, to);
  }
  return to;
}

/** A `closed` thread is terminal except for an explicit reopen. */
export function isThreadClosed(status: ChatThreadStatus): boolean {
  return status === 'closed';
}

/** Whether the human/agent side is the current driver (the bot is muted). */
export function isHumanDriven(status: ChatThreadStatus): boolean {
  return status === 'open' || status === 'assigned';
}

/** Whether the AI bot is the current driver (Phase B). */
export function isAiDriven(status: ChatThreadStatus): boolean {
  return status === 'ai_handling';
}

/**
 * Whether the AI bot may take a turn now. True while it is the driver
 * (`ai_handling`) AND while a thread it escalated still waits UNASSIGNED in the
 * queue (`open`): the bot keeps helping until a human actually takes it over
 * (`assigned`) or it is `closed`. A purely human-initiated `open` thread has no
 * bot configured, so the turn caller no-ops — this predicate only authorizes the
 * attempt. Use this (not `isAiDriven`) to gate a bot turn on an inbound message.
 */
export function isAiAnswerable(status: ChatThreadStatus): boolean {
  return status === 'ai_handling' || status === 'open';
}
